export type ColumnPolicy = "required" | "optional" | "available" | "roleLocked";

export interface PresentableColumn {
  readonly key: string;
  readonly presentation?: ColumnPolicy;
}

export interface StoredTablePresentation {
  readonly schema_revision: number;
  readonly visible_columns: readonly string[];
  readonly column_order: readonly string[];
}

function positionLocked(column: PresentableColumn): boolean {
  return column.presentation === "required" || column.presentation === "roleLocked";
}

/**
 * Applies a principal's ordering only to slots the product lets that principal move.
 *
 * `required` and `roleLocked` columns are anchors. Keeping every declared key in this
 * intermediate order matters: visibility is applied afterwards, so hiding an optional
 * column cannot make an anchor drift into another slot.
 */
export function effectiveColumnOrder<One extends PresentableColumn>(
  declared: readonly One[],
  stored: StoredTablePresentation | undefined,
): readonly One[] {
  const allowed = new Map(declared.map((column) => [column.key, column]));
  const requested = [
    ...(stored?.column_order ?? []).filter((key) => allowed.has(key)),
    ...declared.map((column) => column.key).filter((key) => !stored?.column_order.includes(key)),
  ];
  const movable = requested.flatMap((key) => {
    const column = allowed.get(key);
    return column && !positionLocked(column) ? [column] : [];
  });
  let nextMovable = 0;
  return declared.map((column) =>
    positionLocked(column) ? column : (movable[nextMovable++] ?? column),
  );
}

/**
 * Resolves a stored presentation against what THIS build and THIS caller declare.
 *
 * Unknown and stale keys are discarded. Newly declared columns are appended according to
 * their product policy. A saved key can therefore never select a future field that merely
 * happens to occupy an old array position.
 */
export function effectiveColumns<One extends PresentableColumn>(
  declared: readonly One[],
  schemaRevision: number,
  stored: StoredTablePresentation | undefined,
): readonly One[] {
  const current = stored?.schema_revision === schemaRevision ? stored : undefined;
  const ordered = effectiveColumnOrder(declared, current);
  const allowed = new Set(declared.map((column) => column.key));
  const visible = current
    ? new Set(current.visible_columns.filter((key) => allowed.has(key)))
    : new Set(
        declared
          .filter((column) => (column.presentation ?? "optional") !== "available")
          .map((column) => column.key),
      );

  for (const column of declared) {
    const policy = column.presentation ?? "optional";
    if (policy === "required" || policy === "roleLocked") visible.add(column.key);
  }
  // A row needs one stable accessible name even when every declared column is optional.
  if (visible.size === 0 && declared[0]) visible.add(declared[0].key);
  return ordered.filter((column) => visible.has(column.key));
}

export function defaultVisible(columns: readonly PresentableColumn[]): readonly string[] {
  return columns
    .filter((column) => (column.presentation ?? "optional") !== "available")
    .map((column) => column.key);
}

/** Dragging stays within the movable run bounded by fixed product fields. */
export function columnSegments(
  columns: readonly PresentableColumn[],
  order: readonly string[],
): readonly { readonly locked: boolean; readonly keys: readonly string[] }[] {
  const segments: { locked: boolean; keys: string[] }[] = [];
  for (const key of order) {
    const column = columns.find((one) => one.key === key);
    if (!column) continue;
    const locked = positionLocked(column);
    const previous = segments.at(-1);
    if (!locked && previous && !previous.locked) previous.keys.push(key);
    else segments.push({ locked, keys: [key] });
  }
  return segments;
}

/** A stale callback or a malformed reorder cannot replace another field or cross an anchor. */
export function reorderColumnSegment(
  current: readonly string[],
  segment: readonly string[],
  next: readonly string[],
): readonly string[] {
  const start = segment[0] === undefined ? -1 : current.indexOf(segment[0]);
  if (
    start < 0 ||
    next.length !== segment.length ||
    new Set(next).size !== next.length ||
    !next.every((key) => segment.includes(key)) ||
    !segment.every((key, index) => current[start + index] === key)
  )
    return current;
  const result = [...current];
  result.splice(start, segment.length, ...next);
  return result;
}
